import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import {
  ClimateSensorAndCo2DisconnectStatus,
  DeviceHeatingCircuitIdCommands,
  FanRingCondensatePan,
  Feature,
  FeatureProperties,
  GatewayFirmwareUpdateStatus,
  IsNumberValueProperty,
  RoomCommands,
  RoomProperties,
} from '@vi-iot/client';

import { RemoteDeviceType } from '../commissioning';

import { GatewaySelectors } from './gateway.selectors';

@Injectable({ providedIn: 'root' })
export class GatewayStore {
  state$ = this.store.select(GatewaySelectors.getState());
  devices$ = this.store.select(GatewaySelectors.getDevices());
  assignableDevices$ = this.store.select(GatewaySelectors.getAssignableDevices());
  unAssignableDevices$ = this.store.select(GatewaySelectors.getUnAssignableDevices());

  devicesFlag$ = this.store.select(GatewaySelectors.getDevicesFlag());
  assignableDevicesFlag$ = this.store.select(GatewaySelectors.getAssignableDevicesFlag());
  unassignableDevicesFlag$ = this.store.select(GatewaySelectors.getUnssignableDevicesFlag());
  allGatewayDevicesDetails$ = this.store.select(GatewaySelectors.getAllGatewayDevicesDetails());
  isLowZigbeeSignal$ = this.store.select(GatewaySelectors.getIsLowZigbeeSignal());
  isSingleRoomControlAvailable$ = this.store.select(
    GatewaySelectors.isSingleRoomControlAvailable()
  );
  hasAnyFHTDemandRelayMode$ = this.store.select(GatewaySelectors.hasAnyFHTDemandRelayMode());
  allZigbeeTypeDevices$ = this.store.select(GatewaySelectors.getAllZigbeeTypeDevices());
  climateSensorDevice$ = this.store.select(GatewaySelectors.getClimateSensorDevice());
  repeaterDevices$ = this.store.select(GatewaySelectors.getRepeaterDevices());
  isWifiEnabled$ = this.store.select(GatewaySelectors.selectIsWifiEnabled);
  getVitotrolDevices$ = this.store.select(GatewaySelectors.selectVitotrolDevices());
  getVitotrol300EDevicesNotAssignedToRoom$ = this.store.select(
    GatewaySelectors.getVitotrol300EDevicesNotAssignedToRoom()
  );
  isOnlyOneRemainingGatewayDevice$ = this.store.select(
    GatewaySelectors.isOnlyOneRemainingGatewayDevice()
  );

  getHeaterFanRingCondensatePan$: Observable<FanRingCondensatePan> = this.store.select(
    GatewaySelectors.getHeaterFanRingCondensatePan()
  );

  getDeviceRoles(deviceId: string): Observable<string[]> {
    return this.store.select(GatewaySelectors.getDeviceRoles(deviceId));
  }

  getGatewayFirmwareUpdateStatus(): Observable<GatewayFirmwareUpdateStatus> {
    return this.store.select(GatewaySelectors.getGatewayFirmwareUpdateStatus());
  }

  getGatewayType(): Observable<string> {
    return this.store.select(GatewaySelectors.getGatewayType());
  }

  isDeviceOfType(deviceId: string | string[], modelType: RemoteDeviceType): Observable<boolean> {
    return this.store.select(GatewaySelectors.isDeviceOfType(deviceId, modelType));
  }

  isClimateSensorBatteryLow(deviceId: string): Observable<boolean> {
    return this.store.select(GatewaySelectors.isClimateSensorBatteryLow(deviceId));
  }

  isAirQualitySensorBatteryLow(deviceId: string): Observable<boolean> {
    return this.store.select(GatewaySelectors.isAirQualitySensorBatteryLow(deviceId));
  }

  isRadiatorValveBatteryLow(deviceId: string): Observable<boolean> {
    return this.store.select(GatewaySelectors.isRadiatorValveBatteryLow(deviceId));
  }

  isAnyClimateSensorDischarged(room: Feature<RoomProperties, RoomCommands>): Observable<boolean> {
    return this.store.select(GatewaySelectors.isAnyClimateSensorDischarged(room));
  }

  isAnyRadiatorValveDischarged(room: Feature<RoomProperties, RoomCommands>): Observable<boolean> {
    return this.store.select(GatewaySelectors.isAnyRadiatorValveDischarged(room));
  }
  isAnyRadiatorValveDisconnected(room: Feature<RoomProperties, RoomCommands>): Observable<boolean> {
    return this.store.select(GatewaySelectors.isAnyRadiatorValveDisconnected(room));
  }

  isAnyAirQualitySensorDischarged(
    room: Feature<RoomProperties, RoomCommands>
  ): Observable<boolean> {
    return this.store.select(GatewaySelectors.isAnyAirQualitySensorDischarged(room));
  }

  isMountingModeActive(room: Feature<RoomProperties, RoomCommands>): Observable<number> {
    return this.store.select(GatewaySelectors.isMountingModeActive(room));
  }

  isCSOrCo2SensorNotAvailableWithFHT(
    room: Feature<RoomProperties, RoomCommands>
  ): Observable<ClimateSensorAndCo2DisconnectStatus> {
    return this.store.select(GatewaySelectors.isCSOrCo2SensorNotAvailableWithFHT(room));
  }

  isInRoomTRVInMountingMode(room: Feature<RoomProperties, RoomCommands>): Observable<boolean> {
    return this.store.select(GatewaySelectors.isInRoomTRVInMountingMode(room));
  }

  isMainFHTUnitDisconnected(room: Feature<RoomProperties, RoomCommands>): Observable<boolean> {
    return this.store.select(GatewaySelectors.isMainFHTUnitDisconnected(room));
  }

  getHeatingCircuitOfDevice(
    deviceId: string
  ): Observable<Feature<IsNumberValueProperty, DeviceHeatingCircuitIdCommands>> {
    return this.store.select(GatewaySelectors.selectHeatingCircuitOfDevice(deviceId));
  }

  shouldDisplayWarningHeatingCircuitModal(): Observable<boolean> {
    return this.store.select(GatewaySelectors.shouldDisplayWarningHeatingCircuitModal());
  }

  getAllZigbeeTypeDevicesIds(): Observable<string[]> {
    return this.store.select(GatewaySelectors.getAllZigbeeTypeDevicesIds());
  }

  isRemoteDeviceCommissioned(name: string): Observable<boolean> {
    return this.store.select(GatewaySelectors.isRemoteDeviceCommissioned(name));
  }

  getHeatOnTimeFeature(room: Feature<RoomProperties, RoomCommands>): Observable<FeatureProperties> {
    return this.store.select(GatewaySelectors.getHeatOnTimeFeature(room));
  }

  constructor(private store: Store) {}
}
